/*
  Two States — scroll film engine.
  One requestAnimationFrame loop reads scroll position (no scroll listeners, no IntersectionObservers).
  Choreography constants come from the TAVO 01 brief, generalised from three clips to N chapters.
  Chapters are still photographs / app screenshots with a scroll-driven camera move, not video.
*/
const HOLD = 0.6261;
const WIPE_VH = 90;
const CUT = [0.622, 0.646];
const VIDFADE = [0.646, 0.672];
const EXIT = [0.672, 1.0];
const FIN = [0.78, 0.862];
const TAIL = [0.856, 0.974];
const STAGE_FADE = [0.94, 1.0];
// Screen windows as fractions of a chapter's length (TAVO: in .2348–.2817, out .3913–.4383 of an A–B chapter).
const IN_W = [0.067, 0.267];
const OUT_W = [0.733, 0.933];
const POL_TAU = 6;
const POL_SNAP = 0.004;
const CAM_TAU = 8;

const root = document.documentElement;
const mqMobile = matchMedia('(max-width: 767px)');
const mqReduce = matchMedia('(prefers-reduced-motion: reduce)');

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const segT = (p, a, b) => (b <= a ? (p >= b ? 1 : 0) : clamp01((p - a) / (b - a)));
const smooth = (t) => t * t * (3 - 2 * t);
const r3 = (v) => Math.round(v * 1000) / 1000;

// Memoised style writes; remembers what was written so a mode change can undo exactly that
// (Liquid also writes inline custom properties such as --x/--y, which must survive).
const varCache = new Map();
function setVar(el, name, value) {
  const v = typeof value === 'number' ? String(r3(value)) : value;
  let m = varCache.get(el);
  if (!m) varCache.set(el, (m = {}));
  if (m[name] === v) return;
  m[name] = v;
  el.style.setProperty(name, v);
}
function clearVars(scope) {
  varCache.forEach((m, el) => {
    if (!scope.contains(el)) return;
    Object.keys(m).forEach((k) => el.style.removeProperty(k));
    varCache.delete(el);
  });
}
function setClass(el, name, on) {
  if (el.classList.contains(name) !== on) el.classList.toggle(name, on);
}
function setInert(el, on) {
  if (el.inert !== on) el.inert = on;
}

/* ---------------- Entrance ---------------- */
let entered = false;
let shownAt = performance.now();
function enter() {
  if (entered) return;
  entered = true;
  root.classList.add('is-entered');
  setTimeout(() => root.classList.add('is-settled'), 4000);
}
// While the launch intro (ts-intro.js) covers the page, hold the entrance until it hands over.
const introOn = () => root.classList.contains('ts-intro-on');
const tryEnter = () => { if (!introOn()) enter(); };
if (document.fonts && document.fonts.ready) document.fonts.ready.then(tryEnter);
addEventListener('load', tryEnter);
setTimeout(tryEnter, 2500); // visibility failsafe, independent of rAF
document.addEventListener('ts:intro-done', () => { shownAt = performance.now(); enter(); });
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') shownAt = performance.now();
});

/* ---------------- Mode ---------------- */
let cinema = false;
function applyMode() {
  cinema = !mqMobile.matches && !mqReduce.matches && !!document.querySelector('[data-film]');
  root.classList.toggle('is-cinema', cinema);
  films.forEach((f) => f.reset());
  measureAll();
}
mqMobile.addEventListener('change', applyMode);
mqReduce.addEventListener('change', applyMode);

/* ---------------- Film ---------------- */
class Film {
  constructor(el) {
    this.el = el;
    this.sticky = el.querySelector('.tsf-sticky');
    this.layers = [...el.querySelectorAll('[data-layer]')];
    this.screens = [...el.querySelectorAll('[data-screen]')];
    this.plate = el.querySelector('.tsf-plate');
    this.cutout = el.querySelector('[data-cutout]');
    this.finale = el.querySelector('[data-finale]');
    this.n = this.layers.length;
    // Each chapter's share of the clip span [0, HOLD] follows its weight (data-weight, default 1).
    const weights = this.layers.map((l) => Math.max(0.2, parseFloat(l.dataset.weight) || 1));
    const total = weights.reduce((a, b) => a + b, 0) || 1;
    this.lens = weights.map((w) => (HOLD * w) / total);
    this.starts = this.lens.map((_, k) => this.lens.slice(0, k).reduce((a, b) => a + b, 0));
    this.top = 0;
    this.span = 1;
    this.p = 0;
    this.cam = this.layers.map(() => 0);
    this.cutoutOk = !!this.cutout;
    this.state = {};
    this.probeDirty = true;

    const img = this.cutout && this.cutout.querySelector('img');
    if (img) {
      // Load the cutout late with low priority; hide the dive if it fails.
      this.cutoutOk = false;
      const src = img.getAttribute('data-src');
      const go = () => {
        img.onload = () => { this.cutoutOk = true; this.probeDirty = true; };
        img.onerror = () => { this.cutoutOk = false; this.cutout.classList.add('is-missing'); };
        img.fetchPriority = 'low';
        img.decoding = 'async';
        if (src) img.src = src;
      };
      if (document.readyState === 'complete') setTimeout(go, 1200); else addEventListener('load', () => setTimeout(go, 1200));
    }
    this.layers.forEach((l) => {
      const i = l.querySelector('img');
      if (i && !i.complete) i.addEventListener('load', () => { this.probeDirty = true; }, { once: true });
    });

    // Explicit navigation for fixed states: data-go="start" | "end".
    el.querySelectorAll('[data-go]').forEach((a) => a.addEventListener('click', (e) => {
      if (!cinema) return;
      e.preventDefault();
      this.go(a.dataset.go);
    }));
  }

  reset() {
    clearVars(this.el);
    [...this.layers, ...this.screens, this.finale].forEach((n) => n && n.classList.remove('is-off', 'is-moving'));
    [...this.screens, this.finale].forEach((n) => n && setInert(n, false));
    this.el.querySelectorAll('.pol').forEach((n) => polState.delete(n));
    if (this.cutout) this.cutout.classList.remove('is-diving');
  }

  measure() {
    const rect = this.el.getBoundingClientRect();
    this.top = rect.top + scrollY;
    this.span = Math.max(1, this.el.offsetHeight - innerHeight);
    this.wipe = this.span > 0 ? Math.min(0.24, (WIPE_VH / 100) * innerHeight / this.span) : 0;
    this.phoneRects = this.layers.map((l) => {
      const ph = l.querySelector('.tsf-layer__phone');
      return ph ? ph.getBoundingClientRect() : null;
    });
    this.probeDirty = true;
  }

  go(where) {
    const y = where === 'end' ? this.top + this.span : this.top;
    scrollTo({ top: y, behavior: 'auto' });
  }

  get active() {
    const y = scrollY;
    return y + innerHeight > this.top && y < this.top + this.el.offsetHeight;
  }

  update(dt) {
    const p = clamp01((scrollY - this.top) / this.span);
    const moved = Math.abs(p - this.p) > 1e-5;
    this.p = p;
    const st = this.state;

    // Layers: wipes, stage fade, camera move.
    for (let k = 0; k < this.n; k++) {
      const layer = this.layers[k];
      const start = this.starts[k];
      const L = this.lens[k];
      const rev = k === 0 ? 1 : segT(p, start, start + this.wipe);
      const nextRev = k + 1 < this.n ? segT(p, this.starts[k + 1], this.starts[k + 1] + this.wipe) : 0;
      const fade = 1 - segT(nextRev, STAGE_FADE[0], STAGE_FADE[1]);
      const visible = rev > 0 && fade > 0;
      setClass(layer, 'is-off', !visible);
      setVar(layer, '--r', rev);
      setVar(layer, '--edge', rev > 0 && rev < 1 ? 1 : 0);
      setVar(layer, 'opacity', fade);
      // Camera: smoothed, like the brief's video-time smoothing.
      const target = segT(p, start, start + L);
      this.cam[k] += (target - this.cam[k]) * (1 - Math.exp(-dt * CAM_TAU));
      if (Math.abs(target - this.cam[k]) < 0.002) this.cam[k] = target;
      const t = this.cam[k];
      if (visible) {
        const media = layer.firstElementChild;
        setVar(media, '--cam-s', 1.08 - 0.08 * t);
        setVar(media, '--cam-y', r3((0.5 - t) * 2.4) + 'vh');
      }
      st['r' + k] = rev;
      st['f' + k] = fade;
      st['t' + k] = t;
    }

    // Screens: entrance/exit, callout draw, parallax.
    for (let k = 0; k < this.screens.length; k++) {
      const s = this.screens[k];
      const start = this.starts[k] || 0;
      const L = this.lens[k] || 0;
      // Text enters during the wipe as in the brief, but never before ~70% of the wipe has passed
      // (short chapters would otherwise put copy over the outgoing picture).
      const inA = Math.max(start + IN_W[0] * L, start + this.wipe * 0.7);
      const vin = k === 0 ? 1 : segT(p, inA, inA + (IN_W[1] - IN_W[0]) * L);
      const vout = segT(p, start + OUT_W[0] * L, start + OUT_W[1] * L);
      const vis = vin * (1 - vout);
      setVar(s, '--in', vin);
      setVar(s, '--out', vout);
      setClass(s, 'is-moving', (vin > 0 && vin < 1) || (vout > 0 && vout < 1));
      setClass(s, 'is-off', vis <= 0.001);
      setInert(s, vis < 0.5);
      const c = s.querySelector('.tsf-callout');
      if (c) {
        if (k > 0) setVar(c, '--cin', vin);
        setVar(c, '--par', segT(p, start + (k === 0 ? 0 : IN_W[0] * L), start + OUT_W[1] * L));
      }
    }

    // Finale: cutout handoff, dissolve, dive, title, rows.
    const cut = this.cutoutOk ? segT(p, CUT[0], CUT[1]) : 0;
    const dis = segT(p, VIDFADE[0], VIDFADE[1]);
    const exit = this.cutoutOk ? segT(p, EXIT[0], EXIT[1]) : 0;
    const fin = smooth(segT(p, FIN[0], FIN[1]));
    const tail = segT(p, TAIL[0], TAIL[1]);
    setVar(this.plate, '--dis', dis);
    if (this.cutout) {
      setVar(this.cutout, '--cutin', cut);
      setVar(this.cutout, '--exit', exit);
      setClass(this.cutout, 'is-diving', exit > 0.5);
    }
    if (this.finale) {
      setVar(this.finale, '--fin', fin);
      setVar(this.finale, '--tail', tail);
      setClass(this.finale, 'is-off', fin <= 0.001 && tail <= 0.001);
      setInert(this.finale, tail < 0.5);
    }
    Object.assign(st, { p, cut, dis, exit, fin, tail });

    if (moved) this.probeDirty = true;
    this.updatePolarity(dt, dis);
  }

  /* -------- Pixel-based polarity -------- */
  updatePolarity(dt, dis) {
    const els = [...this.sticky.querySelectorAll('.tsf-screen .pol')];
    if (navEls.length && navOverFilm(this)) els.push(...navEls);
    if (this.probeDirty) {
      this.probeDirty = false;
      this.lums = probe.read(this, els, dis);
    }
    els.forEach((el, i) => {
      let lum = this.lums ? this.lums.get(el) : undefined;
      if (lum === undefined) lum = this.fallbackLum();
      tickPol(el, lum, dt);
    });
  }

  fallbackLum() {
    // Tone of the top-most mostly revealed layer.
    let tone = 'dark';
    for (let k = 0; k < this.n; k++) if ((this.state['r' + k] || 0) > 0.5) tone = this.layers[k].dataset.tone || 'dark';
    const base = tone === 'light' ? 0.8 : 0.15;
    const d = this.state.dis || 0;
    return base * (1 - d) + d;
  }
}

/* ---------------- Probe: 32 x 18 offscreen canvas ---------------- */
const probe = {
  ok: true,
  W: 32,
  H: 18,
  init() {
    try {
      this.c = document.createElement('canvas');
      this.c.width = this.W;
      this.c.height = this.H;
      this.x = this.c.getContext('2d', { willReadFrequently: true, alpha: false });
      if (!this.x) this.ok = false;
    } catch (e) { this.ok = false; }
  },
  read(film, els, dis) {
    if (!this.ok) return null;
    try {
      const { W, H, x } = this;
      const vw = innerWidth, vh = innerHeight;
      const sx = W / vw, sy = H / vh;
      x.fillStyle = '#1b1411';
      x.fillRect(0, 0, W, H);
      for (let k = 0; k < film.n; k++) {
        const layer = film.layers[k];
        const r = film.state['r' + k] || 0;
        const f = film.state['f' + k];
        if (r <= 0 || f <= 0) continue;
        const wipe = layer.dataset.wipe;
        let cx = 0, cy = 0, cw = vw, ch = vh;
        if (wipe === 'right') { cx = vw * (1 - r); cw = vw * r; }
        if (wipe === 'up') { cy = vh * (1 - r); ch = vh * r; }
        x.save();
        x.globalAlpha = f;
        x.beginPath();
        x.rect(cx * sx, cy * sy, Math.max(0.01, cw * sx), Math.max(0.01, ch * sy));
        x.clip();
        x.fillStyle = layer.dataset.bg === 'light' ? '#f6f0e5' : '#1b1411';
        x.fillRect(0, 0, W, H);
        const t = film.state['t' + k] || 0;
        const s = 1.08 - 0.08 * t;
        const ty = (0.5 - t) * 0.024 * vh;
        const img = layer.querySelector('.tsf-layer__media > img');
        if (img && img.complete && img.naturalWidth) {
          // Match object-fit: cover with the camera scale.
          const ir = img.naturalWidth / img.naturalHeight, vr = vw / vh;
          let dw = vw, dh = vh;
          if (ir > vr) dw = vh * ir; else dh = vw / ir;
          dw *= s; dh *= s;
          const ox = (vw - dw) / 2, oy = (vh - dh) / 2 + ty;
          x.drawImage(img, ox * sx, oy * sy, dw * sx, dh * sy);
        }
        const scrim = layer.dataset.scrim;
        if (scrim === 'dark' || scrim === 'light') {
          // Same stops as the CSS scrim on .tsf-layer__media::after.
          const c = scrim === 'dark' ? '27, 20, 17' : '246, 240, 229';
          const a = scrim === 'dark' ? [0.72, 0.38] : [0.8, 0.45];
          const g = x.createLinearGradient(0, 0, W, 0);
          g.addColorStop(0, `rgba(${c}, ${a[0]})`);
          g.addColorStop(0.34, `rgba(${c}, ${a[1]})`);
          g.addColorStop(0.58, `rgba(${c}, 0)`);
          x.fillStyle = g;
          x.fillRect(0, 0, W, H);
        }
        const pr = film.phoneRects && film.phoneRects[k];
        const pimg = layer.querySelector('.tsf-layer__phone img');
        if (pr && pimg && pimg.complete && pimg.naturalWidth) {
          x.drawImage(pimg, pr.left * sx, pr.top * sy, pr.width * sx, pr.height * sy);
        }
        x.restore();
      }
      if (dis > 0) {
        x.globalAlpha = dis;
        x.fillStyle = '#f6f0e5';
        x.fillRect(0, 0, W, H);
        x.globalAlpha = 1;
      }
      const data = x.getImageData(0, 0, W, H).data;
      const out = new Map();
      els.forEach((el) => {
        const b = el.getBoundingClientRect();
        if (!b.width || !b.height) return;
        const x0 = Math.max(0, Math.floor(b.left * sx)), x1 = Math.min(W - 1, Math.ceil(b.right * sx) - 1);
        const y0 = Math.max(0, Math.floor(b.top * sy)), y1 = Math.min(H - 1, Math.ceil(b.bottom * sy) - 1);
        let sum = 0, n = 0;
        for (let yy = y0; yy <= y1; yy++) for (let xx = x0; xx <= x1; xx++) {
          const o = (yy * W + xx) * 4;
          sum += (0.2126 * data[o] + 0.7152 * data[o + 1] + 0.0722 * data[o + 2]) / 255;
          n++;
        }
        if (n) out.set(el, sum / n);
      });
      return out;
    } catch (e) {
      this.ok = false; // tainted canvas or other failure: use the progress fallback
      return null;
    }
  },
};
probe.init();

const polState = new Map();
function tickPol(el, lum, dt) {
  let s = polState.get(el);
  if (!s) { s = { target: lum > 0.5 ? 1 : 0, cur: lum > 0.5 ? 1 : 0 }; polState.set(el, s); }
  if (s.target === 1) { if (lum < 0.46) s.target = 0; } else if (lum > 0.54) s.target = 1;
  s.cur += (s.target - s.cur) * (1 - Math.exp(-dt * POL_TAU));
  if (Math.abs(s.target - s.cur) < POL_SNAP) s.cur = s.target;
  setVar(el, '--pol', s.cur);
}

/* ---------------- Navigation pill polarity outside the film ---------------- */
const navEls = [...document.querySelectorAll('[data-nav] .pol, [data-nav].pol')];
const navEl = document.querySelector('[data-nav]');
// While the sticky stage is pinned, the nav sits over the probed stage.
function navOverFilm(film) {
  return cinema && !!navEl && scrollY >= film.top - 1 && scrollY <= film.top + film.span;
}
// Elsewhere, read the tone of whatever section is under the pill (checked ~10x a second).
let navTone = null, navToneAt = 0;
function navToneFromDom(dt, now) {
  if (!navEl) return;
  if (now - navToneAt > 100) {
    navToneAt = now;
    const b = navEl.getBoundingClientRect();
    const hits = document.elementsFromPoint(b.left + b.width / 2, b.bottom + 2);
    const host = hits.map((h) => h.closest('[data-tone]')).find((h) => h && !navEl.contains(h));
    navTone = host ? host.dataset.tone : (document.body.classList.contains('is-light') ? 'light' : 'dark');
  }
  navEls.forEach((el) => tickPol(el, navTone === 'light' ? 0.9 : 0.1, dt));
}

/* ---------------- Loop ---------------- */
const films = [...document.querySelectorAll('[data-film]')].map((el) => new Film(el));
let lastW = innerWidth, lastH = innerHeight, resizeT = 0, lastMeasure = 0;
function measureAll() { films.forEach((f) => f.measure()); lastMeasure = performance.now(); }
addEventListener('resize', () => { clearTimeout(resizeT); resizeT = setTimeout(measureAll, 120); });
addEventListener('orientationchange', () => setTimeout(measureAll, 120));
addEventListener('load', measureAll);

let last = performance.now();
function frame(now) {
  const dt = Math.min(0.1, (now - last) / 1000);
  last = now;
  if (innerWidth !== lastW || innerHeight !== lastH) { lastW = innerWidth; lastH = innerHeight; clearTimeout(resizeT); resizeT = setTimeout(measureAll, 120); }
  if (now - lastMeasure > 1000) measureAll(); // layout can shift as fonts/images settle
  if (!entered && !introOn() && now - shownAt > 1600) enter();
  if (entered && !root.classList.contains('is-settled') && now - shownAt > 4000) root.classList.add('is-settled');

  let navHandled = false;
  if (cinema && document.visibilityState === 'visible') {
    films.forEach((f) => {
      if (!f.active) return;
      f.update(dt);
      if (navOverFilm(f)) navHandled = true;
    });
  }
  if (!navHandled) navToneFromDom(dt, now);
  requestAnimationFrame(frame);
}

/* Hash links into a film's finale go to the fully readable end state. */
function followHash() {
  if (!cinema || !location.hash) return;
  const t = document.getElementById(decodeURIComponent(location.hash.slice(1)));
  const f = t && films.find((fl) => fl.finale && fl.finale.contains(t));
  if (f) f.go('end');
}
addEventListener('hashchange', followHash);

applyMode();
addEventListener('load', followHash);
requestAnimationFrame(frame);

Object.defineProperty(window, '__ts', {
  value: Object.freeze({
    get cinema() { return cinema; },
    get mobile() { return mqMobile.matches; },
    get reducedMotion() { return mqReduce.matches; },
    get probe() { return probe.ok; },
    get films() { return films.map((f) => ({ p: f.p, span: f.span, wipe: f.wipe, chapters: f.n, starts: f.starts, cutout: f.cutoutOk, state: { ...f.state } })); },
  }),
});
